import { toast } from "sonner";

const SHOPIFY_API_VERSION = "2026-01";
const SHOPIFY_STORE_PERMANENT_DOMAIN = "e38601-2.myshopify.com";
const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
const SHOPIFY_STOREFRONT_TOKEN = "7dc3a3eb2f66fb19af18f3317e9e7d59";

const CUSTOM_SHOPIFY_STOREFRONT_TOKEN = "14dbe478bb1d983b3b5369681203acf3"; //Legacy app
const UNSTABLE_SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/unstable/graphql.json`;

export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    descriptionHtml?: string;
    handle: string;
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
          thumbhash: string | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: {
            amount: string;
            currencyCode: string;
          };
          availableForSale: boolean;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
  };
}

export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (response.status === 402) {
    toast.error("Shopify: Payment required", {
      description: "Shopify API access requires an active billing plan. Visit https://admin.shopify.com to upgrade.",
    });
    return null;
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  if (data.errors) {
    throw new Error(`Shopify API error: ${data.errors.map((e: { message: string }) => e.message).join(", ")}`);
  }

  return data;
}

const COLLECTION_BY_HANDLE_QUERY = `
  query GetCollectionByHandle($handle: String!, $first: Int!, $country: CountryCode) @inContext(country: $country) {
    collection(handle: $handle) {
      id
      title
      products(first: $first) {
        edges {
          node {
            id
            title
            description
            handle
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 5) {
              edges {
                node {
                  url
                  altText
                  thumbhash
                }
              }
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                }
              }
            }
            options {
              name
              values
            }
          }
        }
      }
    }
  }
`;

const PRODUCT_BY_HANDLE_QUERY = `
  query GetProductByHandle($handle: String!, $country: CountryCode) @inContext(country: $country) {
    product(handle: $handle) {
      id
      title
      description
      descriptionHtml
      handle
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 10) {
        edges {
          node {
            url
            altText
            thumbhash
          }
        }
      }
      variants(first: 10) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
      }
      options {
        name
        values
      }
    }
  }
`;

export async function fetchCollectionProducts(handle: string, first = 24, country = "US"): Promise<ShopifyProduct[]> {
  const data = await storefrontApiRequest(COLLECTION_BY_HANDLE_QUERY, { handle, first, country });
  if (!data?.data?.collection) return [];
  return data.data.collection.products.edges;
}

export async function fetchProductByHandle(handle: string, country = "US"): Promise<ShopifyProduct | null> {
  const data = await storefrontApiRequest(PRODUCT_BY_HANDLE_QUERY, { handle, country });
  if (!data?.data?.product) return null;
  return { node: data.data.product };
}

// ── Cart mutations ──

export const CART_QUERY = `
  query cart($id: ID!) {
    cart(id: $id) { id totalQuantity }
  }
`;

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
      }
      userErrors { field message }
    }
  }
`;

const CART_BUYER_IDENTITY_UPDATE_MUTATION = `
  mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
      cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity ) {
        cart {
          id
          checkoutUrl
          cost {
            totalAmount {
              amount
              currencyCode
            }
          }
          lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
        }
        userErrors { field message }
      }
    }
`;

const CART_LINES_ADD_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
      }
      userErrors { field message }
    }
  }
`;

const CART_LINES_UPDATE_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { 
        id
        checkoutUrl
        cost {
          totalAmount {
            amount
            currencyCode
          }
        } 
      }
      userErrors { field message }
    }
  }
`;

const CART_LINES_REMOVE_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { 
        id
        checkoutUrl
        cost {
          totalAmount {
            amount
            currencyCode
          }
        } 
      }
      userErrors { field message }
    }
  }
`;

const VARIANTS_BY_IDS_QUERY = `
  query GetVariantsByIds($ids: [ID!]!, $country: CountryCode) @inContext(country: $country) {
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        price {
          amount
          currencyCode
        }
      }
    }
  }
`;

function formatCheckoutUrl(checkoutUrl: string): string {
  try {
    const url = new URL(checkoutUrl);
    url.searchParams.set("channel", "online_store")
    url.searchParams.set("utm_source", "custom_storefront")
    return url.toString()
  } catch {
    return checkoutUrl;
  }
}

function isCartNotFoundError(userErrors: Array<{ field: string[] | null; message: string }>): boolean {
  return userErrors.some(
    (e) => e.message.toLowerCase().includes("cart not found") || e.message.toLowerCase().includes("does not exist"),
  );
}

export interface CartItem {
  lineId: string | null;
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: { amount: string; currencyCode: string };
  quantity: number;
  selectedOptions: Array<{ name: string; value: string }>;
}

export async function createShopifyCart(
  item: CartItem,
  country = "US",
): Promise<{
  cartId: string;
  checkoutUrl: string;
  lineId: string;
  cost: { totalAmount: { amount: string; currencyCode: string } };
} | null> {
  const data = await storefrontApiRequest(CART_CREATE_MUTATION, {
    input: {
      lines: [{ quantity: item.quantity, merchandiseId: item.variantId }],
      buyerIdentity: {
        countryCode: country,
      },
    },
  });

  if (data?.data?.cartCreate?.userErrors?.length > 0) {
    console.error("Cart creation failed:", data.data.cartCreate.userErrors);
    return null;
  }

  const cart = data?.data?.cartCreate?.cart;
  if (!cart?.checkoutUrl) return null;

  const lineId = cart.lines.edges[0]?.node?.id;
  if (!lineId) return null;

  return { cartId: cart.id, checkoutUrl: formatCheckoutUrl(cart.checkoutUrl), lineId, cost: cart.cost };
}

export async function updateCartBuyerIdentity(
  cartId: string,
  country = "US",
): Promise<{
  success: boolean;
  checkoutUrl?: string;
  cost?: { totalAmount: { amount: string; currencyCode: string } };
  lines?: Array<{ id: string; merchandise: { id: string } }>;
}> {
  const data = await storefrontApiRequest(CART_BUYER_IDENTITY_UPDATE_MUTATION, {
    cartId: cartId,
    buyerIdentity: {
      countryCode: country,
    },
  });

  if (data?.data?.cartBuyerIdentityUpdate?.userErrors.length > 0) {
    console.error("Cart identity update failed:", data.data.cartBuyerIdentityUpdate.userErrors);
    return { success: false };
  }

  return {
    success: true,
    cost: data?.data?.cartBuyerIdentityUpdate?.cart.cost,
    checkoutUrl: data?.data?.cartBuyerIdentityUpdate?.cart.checkoutUrl,
    lines: data?.data?.cartBuyerIdentityUpdate?.cart.lines?.edges?.map(
      (edge: { node: { id: string; merchandise: { id: string } } }) => edge.node,
    ),
  };
}

export async function getVariantPrices(
  variantIds: string[],
  country = "US",
): Promise<Record<string, { amount: string; currencyCode: string }> | null> {
  const data = await storefrontApiRequest(VARIANTS_BY_IDS_QUERY, {
    ids: variantIds,
    country: country,
  });

  if (!data?.data?.nodes) return null;

  const priceMap: Record<string, { amount: string; currencyCode: string }> = {};
  data.data.nodes.forEach((variant: { id: string; price: { amount: string; currencyCode: string } }) => {
    if (variant && variant.price) {
      priceMap[variant.id] = variant.price;
    }
  });

  return priceMap;
}

export async function addLineToShopifyCart(
  cartId: string,
  item: CartItem,
): Promise<{
  success: boolean;
  checkoutUrl?: string,
  lineId?: string;
  cartNotFound?: boolean;
  cost?: { totalAmount: { amount: string; currencyCode: string } };
}> {
  const data = await storefrontApiRequest(CART_LINES_ADD_MUTATION, {
    cartId,
    lines: [{ quantity: item.quantity, merchandiseId: item.variantId }],
  });

  const userErrors = data?.data?.cartLinesAdd?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) {
    console.error("Add line failed:", userErrors);
    return { success: false };
  }

  const lines = data?.data?.cartLinesAdd?.cart?.lines?.edges || [];
  const newLine = lines.find(
    (l: { node: { id: string; merchandise: { id: string } } }) => l.node.merchandise.id === item.variantId,
  );
  return { 
    success: true,
    checkoutUrl: data?.data?.cartLinesAdd?.cart.checkoutUrl, 
    lineId: newLine?.node?.id, 
    cost: data?.data?.cartLinesAdd?.cart.cost 
  };
}

export async function updateShopifyCartLine(
  cartId: string,
  lineId: string,
  quantity: number,
): Promise<{
  success: boolean;
  checkoutUrl?: string;
  cartNotFound?: boolean;
  cost?: { totalAmount: { amount: string; currencyCode: string } };
}> {
  const data = await storefrontApiRequest(CART_LINES_UPDATE_MUTATION, {
    cartId,
    lines: [{ id: lineId, quantity }],
  });

  const userErrors = data?.data?.cartLinesUpdate?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) {
    console.error("Update line failed:", userErrors);
    return { success: false };
  }
  return { 
    success: true, 
    checkoutUrl: data?.data?.cartLinesUpdate?.cart.checkoutUrl,
    cost: data?.data?.cartLinesUpdate?.cart.cost 
  };
}

export async function removeLineFromShopifyCart(
  cartId: string,
  lineId: string,
): Promise<{
  success: boolean;
  checkoutUrl?: string;
  cartNotFound?: boolean;
  cost?: { totalAmount: { amount: string; currencyCode: string } };
}> {
  const data = await storefrontApiRequest(CART_LINES_REMOVE_MUTATION, {
    cartId,
    lineIds: [lineId],
  });

  const userErrors = data?.data?.cartLinesRemove?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) {
    console.error("Remove line failed:", userErrors);
    return { success: false };
  }
  return { 
    success: true, 
    checkoutUrl: data?.data?.cartLinesRemoves?.cart.checkoutUrl,
    cost: data?.data?.cartLinesRemove?.cart.cost 
  };
}

// Newsletter subscribe via Storefront API (using customerCreate with acceptsMarketing)

const CUSTOMER_SUBSCRIBE = `
  mutation customerSubscribe($email: String!) {
		customerEmailMarketingSubscribe(email: $email) {
      customer {
        id
      }
      customerUserErrors {
        field
        message
        code
      }
    }
  }
`;

export async function subscribeToNewsletter(email: string): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(UNSTABLE_SHOPIFY_STOREFRONT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": CUSTOM_SHOPIFY_STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: CUSTOMER_SUBSCRIBE,
        variables: {
          email: email,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.errors) {
      throw new Error(data.errors.map((e: { message: string }) => e.message).join(", "));
    }

    const userErrors = data?.data?.customerEmailMarketingSubscribe?.customerUserErrors || [];
    if (userErrors.length > 0) {
      // If customer already exists / is already subscribed, treat as success
      if (userErrors[0].code === "TAKEN" || userErrors[0].code === "CUSTOMER_DISABLED") {
        return { success: true };
      }
      return { success: false, error: userErrors[0].message };
    }

    return { success: true };
  } catch (err) {
    console.error("Newsletter subscribe error:", err);
    return { success: false, error: "Something went wrong. Please try again." };
  }
}
